import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Brain, Crown, Zap, Infinity as InfinityIcon } from 'lucide-react';

/**
 * Small status tile that surfaces the user's monthly AI-hint quota.
 *
 * Free users:  shows `N / 3 used`, a green→amber→red progress bar, and an
 *              "Upgrade for unlimited" CTA that gets visually emphasised
 *              once the counter hits zero — that exhausted state is the
 *              highest-intent upgrade moment in the entire funnel.
 * Pro users:   collapses to a calm "Unlimited" badge.
 *
 * Owns NO state — `quota` comes from `GET /api/ai/hint-quota` and `onUpgrade`
 * is provided by the parent page.
 */
export const ProfileHintQuotaCard = ({ quota, onUpgrade }) => {
  if (!quota) return null;

  // Pro tier — calm "Unlimited" state.
  if (quota.tier === 'pro') {
    return (
      <Card
        className="bg-[#1A1A1A] border-gray-800 mb-6"
        data-testid="profile-hint-quota-card"
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#00FF00]" />
              AI Hints
            </CardTitle>
            <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/40">
              <InfinityIcon className="w-3 h-3 mr-1" />
              UNLIMITED
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-400" data-testid="hint-quota-pro-line">
            Pro: unlimited AI coach hints across every room.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Free tier — the interesting case.
  const used = quota.used ?? 0;
  const limit = quota.limit ?? 3;
  const remaining = quota.remaining ?? Math.max(0, limit - used);
  const exhausted = remaining <= 0;
  const pct = Math.min(100, Math.round((used / limit) * 100));

  // Progress bar: green when fresh, amber at half, red when exhausted.
  const barColor = (() => {
    if (exhausted) return 'bg-[#FF0055]';
    if (remaining === 1) return 'bg-[#FF9500]';
    return 'bg-[#00FF00]';
  })();

  return (
    <Card
      className={`bg-[#1A1A1A] mb-6 ${
        exhausted
          ? 'border-[#FF0055]/40 ring-1 ring-[#FF0055]/20'
          : 'border-gray-800'
      }`}
      data-testid="profile-hint-quota-card"
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Brain className="w-5 h-5 text-[#00BFFF]" />
            AI Hints — Free monthly quota
          </CardTitle>
          <Badge
            className={
              exhausted
                ? 'bg-[#FF0055]/15 text-[#FF0055] border-[#FF0055]/40'
                : 'bg-[#0A0A0A] text-gray-300 border-gray-700'
            }
            data-testid="hint-quota-badge"
          >
            {used} / {limit} used
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div className="w-full h-2 bg-[#0A0A0A] border border-gray-800 rounded-full overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all duration-500`}
            style={{ width: `${pct}%` }}
            data-testid="hint-quota-progress"
          />
        </div>

        {exhausted ? (
          <>
            <p className="text-sm text-gray-300" data-testid="hint-quota-exhausted-line">
              You've used all{' '}
              <span className="text-[#FF0055] font-semibold">{limit}</span> AI hints
              for this month. They reset on the 1st — or get unlimited hints right now with Pro.
            </p>
            <Button
              data-testid="hint-quota-upgrade-btn"
              onClick={onUpgrade}
              className="w-full sm:w-auto bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade for unlimited hints — $15/mo
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-400" data-testid="hint-quota-remaining-line">
              <span className="text-[#00FF00] font-semibold">{remaining}</span>{' '}
              {remaining === 1 ? 'hint' : 'hints'} left this month. Resets on the 1st.
            </p>
            <Button
              data-testid="hint-quota-upgrade-btn"
              onClick={onUpgrade}
              variant="outline"
              size="sm"
              className="border-[#00FF00]/40 text-[#00FF00] hover:bg-[#00FF00]/10"
            >
              <Zap className="w-4 h-4 mr-2" />
              Get unlimited with Pro
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};
